<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { usePayrollStore } from '@/store/payroll-data'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { saveFile } from '@/app/core/services/files'
import { useI18n } from 'vue-i18n'

import * as XLSX from 'xlsx-js-style'
import {
  getListImports,
  getFieldsImport,
  ImportRequest,
  configureImportRequest
} from '@/app/core/services/import-requests'
import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DLoading,
  DTextAreaField,
  DFileUploader,
  PlusIcon,
  XIcon
} from 'front-library'
usePayrollChange(() => window.location.reload())

/* router, notifications */
const router = useRouter()
const notifications = useNotificationsStore()
const storePayroll = usePayrollStore()
const { t } = useI18n()

/* Datos */
const openAddFields = ref<boolean>(false)
const divAddFields = ref<any>(null)
const contentFile = ref<any>()

/* Datos Listas */
const itemsImports = ref<any[]>([])
const searchImports = ref<string>('')
const itemsActions = ref<object[]>([])
const searchActions = ref<string>('')
const itemsFields = ref<any[]>([])
const removedFields = ref<any[]>([])
const itemsRestrictions = ref<any[]>([])

/* Datos componente DLoading */
const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .indexOf(
            search
              .toString()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
          ) > -1
    ))
})

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  tableCode: string().required(
    t('form.valid.fieldRequired', { field: t('import.valid.import') })
  ),
  accionId: string().required(
    t('form.valid.fieldRequired', { field: t('import.valid.action') })
  ),
  fileUrl: string()
    .required(t('import.valid.fileRequired'))
    .test('fileFormat', t('import.valid.unsupportedFormat'), value => {
      const allowedExtensions = ['xlsx']
      let extensionFile: any
      if (value !== undefined) {
        extensionFile = value.split('.').pop()
      }
      return allowedExtensions.includes(extensionFile)
    }),
  observations: string().required(
    t('form.valid.fieldRequired', { field: t('import.valid.observations') })
  ),
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<ImportRequest>({
  validationSchema
})

/* Definicion de  campos del formulario */
const { value: tableCode } = useField<string>(ref('tableCode'))
const { value: accionId } = useField<string>(ref('accionId'))
const { value: fileUrl } = useField<any>(ref('fileUrl'))
const { value: observations } = useField<string>(ref('observations'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any, { resetForm }) => {
  void uploadFile(values)
})

const startImport = async (values: any) => {
  try {
    await configureImportRequest(values)
    notifications.addNotification({
      title: t('import.notification.title'),
      text: t('import.notification.description'),
      type: 'success',
      time: 10000
    })
    loading.value = false
    void router.push({ name: 'ListImportRequests' })
  } catch (error: any) {
    notificationError(error, 'import', t, notifications, loading)
    await router.push({ name: 'ListImportRequests' })
  }
}

const uploadFile = async (data: any) => {
  loading.value = true
  messageLoading.value = t('import.notification.messageLoading')
  fileUrl.value = await saveFile(
    contentFile.value.content,
    '',
    'imports',
    import.meta.env.VITE_BUCKET_UPLOAD || 'sbx-aps-temporary-bucket'
  )
  if (fileUrl.value !== '') {
    data.fileUrl = fileUrl.value
    void startImport(data)
  }
}

const deleteField = (index: number, field: object) => {
  itemsFields.value.splice(index, 1)
  removedFields.value.push(field)
}

const addField = (index: number, field: object) => {
  itemsFields.value.push(field)
  removedFields.value.splice(index, 1)
  if (removedFields.value.length === 0) openAddFields.value = false
}

const getFields = async () => {
  if (
    tableCode.value === undefined ||
    accionId.value === undefined ||
    accionId.value === ''
  )
    return

  const action = itemsActions.value
    .filter((_: any) => _.id === accionId.value)
    .map((_: any) => _.code)[0]

  itemsFields.value = await getFieldsImport(tableCode.value, accionId.value)

  if (action === 'update') {
    removedFields.value = itemsFields.value.filter(
      (_: any) => _.isRequired !== true
    )

    itemsFields.value = itemsFields.value.filter(
      (_: any) => _.isRequired === true
    )
  } else {
    removedFields.value = []
  }
}

const generateTemplate = () => {
  if (tableCode.value && accionId.value) {
    const ws = XLSX.utils.aoa_to_sheet([])
    itemsFields.value.forEach((item, i) => {
      const cellRef = XLSX.utils.encode_cell({
        c: i,
        r: 0
      })
      XLSX.utils.sheet_add_aoa(ws, [[item.name]], {
        origin: cellRef
      })
      if (item.description) {
        ws[cellRef].c = ws[cellRef].c || []
        ws[cellRef].c.hidden = true
        ws[cellRef].c.push({ t: item.description })
      }
      if (item.isRequired) {
        ws[cellRef].s = {
          fill: {
            fgColor: { rgb: 'F5B7B1' }
          }
        }
      }
    })
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
    const filterActionName: any = itemsActions.value.filter(
      (_: any) => _.id === accionId.value
    )
    const nameAction = filterActionName[0].name.toLowerCase()
    const filterImportName = itemsImports.value.filter(
      (_: any) => _.code === tableCode.value
    )
    const nameTable = filterImportName[0].name
      .split('/')[1]
      .split(' ')
      .join('-')
      .toLowerCase()

    XLSX.writeFile(
      wb,
      `${t('import.fileName')}-${nameAction as string}${
        nameTable as string
      }.xlsx`
    )
  } else {
    notifications.addNotification({
      title: t('notification.requiredFields'),
      text: t('notification.requiredFieldsText', { fields: '.' }),
      type: 'error',
      time: 10000
    })
  }
}

const openWindowFields = () => {
  if (removedFields.value.length > 0) {
    openAddFields.value = true
    setTimeout(() => {
      divAddFields.value.focus()
    }, 1)
  }
}

const onCloseAddFields = () => {
  openAddFields.value = false
}

const back = () => {
  void router.push({ name: 'ListImportRequests' })
}

const getListAction = (code: any) => {
  accionId.value = ''
  itemsFields.value = []
  const valueImport = itemsImports.value.filter((_: any) => _.code === code)
  if (valueImport.length > 0) {
    itemsActions.value = valueImport[0].actions
    itemsRestrictions.value = []
    const isoCode = storePayroll?.payroll?.country?.iso_code
    itemsRestrictions.value = valueImport[0].restrictions[isoCode as string]
  } else {
    notifications.addNotification({
      title: t('notification.notFound'),
      text: t('import.notification.notFound'),
      type: 'error',
      time: 10000
    })
  }
}

onMounted(async () => {
  try {
    itemsImports.value = await getListImports()
    if (itemsImports.value === undefined) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('import.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    }
  } catch (error: any) {
    notificationError(error, 'import', t, notifications, loading)
  }
})
</script>
<template>
  <div class="h-full px-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <div class="bg-gray-50 pb-10">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('import.create')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 12rem)">
      <DCard>
        <div class="grid grid-cols-3">
          <div class="relative flex flex-col col-span-4 lg:col-span-1 p-15">
            <!--Titulo y boton añadir campos -->
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-md font-medium leading-6 text-gray-900">
                {{ t('import.fields.title') }}
              </h3>
              <div
                @click="openWindowFields"
                class="p-2.5 bg-blue-100 rounded-md cursor-pointer">
                <PlusIcon class="h-5 w-5 text-blue-600" />
              </div>
              <!-- Ventana Campos eliminados-->
              <div
                @focusout="onCloseAddFields"
                tabindex="0"
                ref="divAddFields"
                v-if="openAddFields"
                class="z-20 absolute bg-white border border-gray-100 left-0 top-10 mt-2 right-0 w-full rounded-xl shadow-xl outline-none overflow-hidden">
                <div
                  class="window-menu w-full flex flex-wrap gap-3 max-h-36 overflow-auto p-5">
                  <div
                    v-for="(field, index) in removedFields"
                    :key="index"
                    class="flex flex-row items-center justify-between bg-gray-100 hover:bg-gray-200 px-2 py-3 h-5 rounded-xl cursor-default">
                    <div>
                      <p
                        class="text-center tracking-wider text-sm text-gray-600 max-w-xs truncate ...">
                        {{ field.name }}
                      </p>
                    </div>
                    <div>
                      <PlusIcon
                        @click="addField(index, field)"
                        class="w-5 h-5 text-gray-500 cursor-pointer" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!--Container campos importación -->
            <div class="max-h-80 overflow-auto flex flex-wrap gap-2 py-2">
              <div
                v-for="(field, index) in itemsFields"
                :key="index"
                class="flex flex-row items-center justify-between px-2 py-3 h-5 rounded-xl cursor-default"
                :class="[
                  field.isRequired ? 'bg-red-100' : 'bg-gray-200',
                  field.isRequired ? 'hover:bg-red-200' : 'hover:bg-gray-300'
                ]">
                <div>
                  <p
                    :class="[
                      field.isRequired ? 'text-red-600' : 'text-gray-600'
                    ]"
                    class="text-center tracking-wider text-sm max-w-xs truncate ...">
                    <span v-if="field.isRequired" class="text-lg">•</span>
                    {{ field.name }}
                  </p>
                </div>
                <div v-if="!field.isRequired">
                  <XIcon
                    @click="deleteField(index, field)"
                    :class="[
                      field.isRequired ? 'text-red-500' : 'text-gray-500'
                    ]"
                    class="w-5 h-5 cursor-pointer" />
                </div>
              </div>
            </div>
            <!--Boton descargar plantilla -->
            <div class="flex justify-center items-center mt-3">
              <button
                @click.prevent="generateTemplate"
                class="bg-green-100 text-green-700 font-medium inline-flex justify-center items-center py-1.5 px-4 border border-transparent shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 hover:text-white hover:bg-green-600 focus:ring-green-500">
                <span class="mr-3 text-xl">•</span>
                {{ t('import.downloadFile') }}
              </button>
            </div>
          </div>
          <div class="col-span-4 lg:col-span-2 p-2 ml-5">
            <div class="grid grid-cols-6 gap-3">
              <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                <DSelectFieldSearch
                  :label="t('import.fields.import')"
                  v-model="tableCode"
                  :options="filterOptions(itemsImports, searchImports)"
                  @filter="searchImports = $event"
                  :required="true"
                  :valueKey="'code'"
                  @change="getListAction(tableCode), getFields()"
                  :error="errors.tableCode" />
              </div>
              <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                <DSelectFieldSearch
                  :label="t('import.fields.action')"
                  v-model="accionId"
                  :options="filterOptions(itemsActions, searchActions)"
                  @filter="searchActions = $event"
                  :required="true"
                  @change="getFields()"
                  :error="errors.accionId" />
              </div>
              <div
                class="col-span-6 sm:col-span-6 lg:col-span-6 bg-blue-50 rounded-xl px-5 py-3 border-2 border-blue-100">
                <h1 class="text-blue-600">
                  {{ t('import.instructions.title') }}
                </h1>
                <ul
                  class="text-blue-500 px-5 py-2 list-inside ..."
                  style="list-style-type: disc">
                  <li>{{ t('import.instructions.item1') }}</li>
                  <li>{{ t('import.instructions.item2') }}</li>
                  <li>{{ t('import.instructions.item3') }}</li>
                  <li>{{ t('import.instructions.item4') }}</li>
                  <li>{{ t('import.instructions.item5') }}</li>
                  <li>{{ t('import.instructions.item6') }}</li>
                  <li>{{ t('import.instructions.item7') }}</li>
                  <li>{{ t('import.instructions.item8') }}</li>
                </ul>
              </div>
              <div
                v-if="itemsRestrictions?.length > 0"
                class="col-span-6 sm:col-span-6 lg:col-span-6 bg-orange-50 rounded-xl px-5 py-3 border-2 border-orange-100">
                <h1 class="text-orange-600 mb-3">
                  {{ t('import.instructions.restriction') }}
                </h1>
                <ul
                  v-for="(restriction, index) in itemsRestrictions"
                  :key="index"
                  class="text-orange-500 px-5 list-inside ..."
                  style="list-style-type: disc">
                  <li>{{ restriction }}</li>
                </ul>
              </div>
              <div class="col-span-6 sm:col-span-6 lg:col-span-6">
                <DFileUploader
                  label=""
                  v-model="fileUrl"
                  mode="edit"
                  :helpText="t('import.fields.file')"
                  @file="contentFile = $event"
                  :error="errors.fileUrl" />
              </div>
              <div class="col-span-6 sm:col-span-6 lg:col-span-6">
                <DTextAreaField
                  v-model="observations"
                  :required="true"
                  :error="errors.observations"
                  :label="t('import.fields.observations')" />
              </div>
            </div>
          </div>
          <hr class="col-span-3 mb-5 mt-10" />
          <div class="col-span-3 flex justify-end">
            <DBtn
              color="secondary"
              class="mr-3"
              @click="back"
              v-html="t('form.button.cancel')" />
            <DBtn
              @click="onSubmit"
              v-html="t('form.button.create', { form: t('import.request') })" />
          </div>
        </div>
      </DCard>
    </form>
  </div>
</template>
<style scoped>
.window-menu {
  min-height: 100px;
}
</style>
